import React, { useEffect, useState, useRef, useCallback, useLayoutEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import chatService from '../../services/chat.service';
import config from '../../config';
import { useSelector } from 'react-redux';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import DotSpinner from '../../components/shared/DotSpinner';
import { calculateAge } from '../../utils/calculateAge';
import { formatTime } from '../../utils/formatTime';
import { CheckIsBlocked } from './CheckIsBlocked';
import { useObserver } from '../../hooks/useObserver';
import { useChat } from '../../context/ChatProvider';
import { toast } from 'react-toastify';
import SendGiftsModal from '../../components/gifts/SendGiftsModal';
import { apiGifts } from '../../api/apiGifts.api';

function mergeMessages(currentMessages = [], incomingMessages = []) {
  const messagesMap = new Map();

  [...currentMessages, ...incomingMessages].forEach((message) => {
    if (!message?._id) return;

    const messageId = String(message._id);
    const previousMessage = messagesMap.get(messageId) || {};
    messagesMap.set(messageId, { ...previousMessage, ...message });
  });

  return Array.from(messagesMap.values()).sort(
    (left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime()
  );
}

function shouldRedirectToChatList(error) {
  const status = error?.response?.status;
  const message = error?.response?.data?.message;

  return status === 404 || message === 'Chat partner not found' || message === 'Chat not found';
}

function ChatView() {
  const navigate = useNavigate();
  const location = useLocation();
  const axiosPrivate = useAxiosPrivate();

  const { chat_id } = useParams();
  const { userId: currentUserId } = useSelector((state) => state.auth);
  const [partnerId, setPartnerId] = useState(null);
  const { markChatAsRead } = useChat();

  const [isOpenPresentShop, setIsOpenPresentShop] = useState(false);

  const [otherParticipant, setOtherParticipant] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isFirstMessage, setIsFirstMessage] = useState(false);
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [messageInput, setMessageInput] = useState('');

  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);

  const messagesContainerRef = useRef(null);
  const loadedPagesRef = useRef(new Set());
  const pageRef = useRef(1);
  const totalItemsRef = useRef(null);
  const messagesCountRef = useRef(0);

  const [totalItems, setTotalItems] = useState(null);
  const [page, setPage] = useState(1);

  const showFirstMessagePrompt = isFirstMessage && messages.length === 0;

  const isLoadingMessageRef = useRef(false);

  const latestMessageRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const messageInputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    const container = messagesContainerRef.current;

    if (!container) return;

    container.scrollTop = container.scrollHeight;
  }, []);

  useLayoutEffect(() => {
    const latestMessage = messages[messages.length - 1];

    if (!latestMessage) {
      latestMessageRef.current = null;
      return;
    }

    const latestCreatedAt = new Date(latestMessage.createdAt).getTime();

    if (latestMessageRef.current !== null && latestCreatedAt > latestMessageRef.current) {
      scrollToBottom();

      requestAnimationFrame(() => {
        scrollToBottom();
      });
    }

    latestMessageRef.current = latestCreatedAt;
  }, [messages, scrollToBottom]);

  useLayoutEffect(() => {
    if (!isInitialLoadRef.current) return;
    if (!messages.length) return;

    const container = messagesContainerRef.current;
    if (!container) return;

    isInitialLoadRef.current = false;

    container.scrollTop = container.scrollHeight;

    requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;

      setTimeout(() => {
        container.scrollTop = container.scrollHeight;
      }, 50);
    });
  }, [messages]);

  const fetchChatData = useCallback(
    async (nextPage, { force = false } = {}) => {
      if (!chat_id || isLoadingMessageRef.current) return;
      if (!force && loadedPagesRef.current.has(nextPage)) return;

      isLoadingMessageRef.current = true;
      loadedPagesRef.current.add(nextPage);

      const prevHeight = messagesContainerRef.current?.scrollHeight ?? 0;

      try {
        const chatData = await chatService.getChatHistory(axiosPrivate, chat_id, nextPage);
        const history = chatData.data.chats;

        if (!history?.partner?._id) {
          navigate(config.ROUTES.CHATS.LIST, { replace: true });
          return;
        }

        setPartnerId(history.partner._id);

        setOtherParticipant(history.partner);
        setMessages((prevMessages) => mergeMessages(prevMessages, history.items));
        setIsFirstMessage(history.isFirstMessage && history.items.length === 0);

        setTotalItems(history.total);
        totalItemsRef.current = history.total;
        setPage(nextPage);
        pageRef.current = nextPage;

        requestAnimationFrame(() => {
          const container = messagesContainerRef.current;
          if (!container) return;

          if (nextPage === 1) {
            container.scrollTop = container.scrollHeight;
            return;
          }

          const newHeight = container.scrollHeight;
          container.scrollTop += newHeight - prevHeight;
        });
      } catch (error) {
        loadedPagesRef.current.delete(nextPage);

        if (shouldRedirectToChatList(error)) {
          navigate(config.ROUTES.CHATS.LIST, { replace: true });
          toast.error('Чат не найден. Возможно, он был удалён.');
          return;
        }

        console.error('Error fetching chat data:', error);
      } finally {
        isLoadingMessageRef.current = false;
      }
    },
    [axiosPrivate, chat_id, navigate]
  );

  const targetRef = useObserver({
    root: messagesContainerRef.current,
    onIntersecting: () => {
      if (isLoadingChat) return;

      if (messagesCountRef.current === 0) return;

      if (totalItemsRef.current !== null && messagesCountRef.current >= totalItemsRef.current) {
        return;
      }

      fetchChatData(pageRef.current + 1);
    },
    rootMargin: '100px 0px 0px 0px'
  });

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    totalItemsRef.current = totalItems;
  }, [totalItems]);

  useEffect(() => {
    messagesCountRef.current = messages.length;
  }, [messages.length]);

  const syncChatReadState = useCallback(() => {
    if (!currentUserId || !chat_id) return;

    chatService.markAsRead(chat_id, currentUserId);
    markChatAsRead(chat_id);
  }, [chat_id, currentUserId, markChatAsRead]);

  useEffect(() => {
    if (!currentUserId || !chat_id) {
      navigate('/');
      return;
    }

    chatService.joinChat(chat_id);
    syncChatReadState();

    const unsubscribeMessage = chatService.onMessage((newMessages) => {
      if (String(newMessages.chatId) !== String(chat_id)) {
        return;
      }
      console.log(newMessages);

      const items = newMessages.data.items || [];

      setMessages((prevMessages) => mergeMessages(prevMessages, items));

      if (items.length > 0) {
        setIsFirstMessage(false);
      }

      const hasUnreadIncoming = items.some(
        (message) => String(message.from) !== String(currentUserId) && !message.readAt
      );

      if (hasUnreadIncoming) {
        syncChatReadState();
      }
    });

    const unsubscribeMessageRead = chatService.onMessageRead((data) => {
      if (String(data.chatId) !== String(chat_id)) {
        return;
      }

      if (data.ids.length) {
        setMessages((prevMessages) =>
          prevMessages.map((msg) => (data.ids.includes(msg._id) ? { ...msg, readAt: new Date().toISOString() } : msg))
        );
      }
    });

    const unsubscribeTyping = chatService.onTyping((data) => {
      if (String(data.chatId) !== String(chat_id)) {
        return;
      }
      setIsTyping(data.isTyping);
    });

    return () => {
      chatService.leaveChat(chat_id);
      unsubscribeMessage();
      unsubscribeTyping();
      unsubscribeMessageRead();
    };
  }, [chat_id, currentUserId, navigate, scrollToBottom, syncChatReadState]);

  useEffect(() => {
    loadedPagesRef.current.clear();
    pageRef.current = 1;
    totalItemsRef.current = null;
    messagesCountRef.current = 0;
    setOtherParticipant(null);
    setMessages([]);
    setIsFirstMessage(false);
    setTotalItems(null);
    setPage(1);
    setIsLoadingChat(true);
    fetchChatData(1).finally(() => {
      setIsLoadingChat(false);
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    });
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [chat_id, fetchChatData, scrollToBottom]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setMessageInput(value);

    if (!isTypingRef.current) {
      chatService.startTyping(chat_id, currentUserId);
      isTypingRef.current = true;
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    if (value.trim().length === 1) {
      scrollToBottom();
    }

    typingTimeoutRef.current = setTimeout(() => {
      chatService.stopTyping(chat_id, currentUserId);
      isTypingRef.current = false;
    }, 1500);
  };

  const handleSendMessage = () => {
    const text = messageInput.trim();

    if (!text) return;

    chatService.sendMessage(chat_id, currentUserId, text);

    setMessageInput('');
    chatService.stopTyping(chat_id, currentUserId);

    if (isFirstMessage) {
      setIsFirstMessage(false);
    }

    messageInputRef.current?.focus();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const onSendGiftToStartChatting = async (giftId) => {
    if (!partnerId) return;
    try {
      const res = await apiGifts.sendGift(axiosPrivate, { recipientId: partnerId, giftId });
      setIsOpenPresentShop(false);

      if (res.data.data.ok) {
        loadedPagesRef.current.delete(1);
        setIsLoadingChat(true);
        fetchChatData(1, { force: true }).finally(() => {
          setIsLoadingChat(false);
          requestAnimationFrame(() => scrollToBottom());
        });
      }
    } catch (error) {
      console.error('[Profile] Error sending gift:', error);
      toast.error(error.response?.data?.message || 'Ошибка отправки подарка');
    }
  };

  if (isLoadingChat) {
    return (
      <div className="flex items-center justify-center min-h-[100vh]">
        <DotSpinner />
      </div>
    );
  }

  return (
    <CheckIsBlocked userId={otherParticipant?._id}>
      <SendGiftsModal
        isOpen={isOpenPresentShop}
        onClose={() => setIsOpenPresentShop(false)}
        renderTitle={() => (
          <h2 className="text-[18px] font-semibold text-white text-center leading-[1.5] font-raleway">
            Обозначь себя, сделав человеку приятно. Это шаг к новым знакомствам!
          </h2>
        )}
        onSend={onSendGiftToStartChatting}
      />
      <div className="w-[100vw] mb-[0px] h-full flex justify-start items-center flex-col relative">
        <div
          className="flex items-center justify-center text-white text-[24px] w-full px-4 pb-3 pt-[21px]
            border-b border-[#233636]"
        >
          <div className="w-[100vw] flex items-center justify-center">
            <div className="w-full flex justify-between items-center relative">
              <div className="flex items-center gap-[10px]">
                <img
                  src="/icons/Button-back.svg"
                  onClick={() => {
                    if (location.state?.from?.startsWith(config.ROUTES.NOTIFICATIONS)) {
                      navigate(config.ROUTES.NOTIFICATIONS);
                      return;
                    }
                    navigate(config.ROUTES.CHATS.LIST);
                  }}
                  className="w-[44px] h-[44px]"
                />
                <button
                  onClick={() => setIsOpenPresentShop(true)}
                  className="size-[44px] flex items-center justify-center rounded-full border-[2px] border-white bg-black/30 shrink-0"
                >
                  <img src="/icons/gifts.svg" alt="chat" />
                </button>
              </div>
              <p className="flex-1 min-w-0 text-center truncate">
                {otherParticipant?.name || '...'}, {calculateAge(otherParticipant?.birthDay)} лет
              </p>
              <img
                src={otherParticipant?.photo || config.FALLBACK_AVATAR}
                className="w-[44px] h-[44px] rounded-full shrink-0 object-cover cursor-pointer"
                onClick={() => {
                  navigate(config.ROUTES.CHATS.PROFILE(otherParticipant?._id), {
                    state: {
                      from: `/chats/${chat_id}`
                    }
                  });
                }}
              />
            </div>
          </div>
        </div>
        <div
          ref={messagesContainerRef}
          className="w-full h-full flex-1 flex justify-start items-center flex-col text-white px-4 pt-4 pb-5 font-raleway overflow-y-auto"
        >
          <div ref={targetRef} className="h-2" />
          {messages?.map((message, index) => {
            const isCurrentUser = message.from === currentUserId;
            const isTextType = message.type === 'text';
            return (
              <div
                key={message._id}
                className={`${
                  isCurrentUser
                    ? 'self-end bg-[#0A2438] border border-[#233636] rounded-tr-[16px] rounded-tl-[16px] rounded-bl-[16px]'
                    : 'self-start bg-[#032A2A] border border-[#233636] rounded-tr-[16px] rounded-tl-[16px] rounded-br-[16px]'
                } 
                            relative flex max-w-[90%] min-w-[80px] mt-[16px] leading-[1.5] p-[10px] pb-[30px]`}
              >
                {isTextType ? (
                  <div className={`flex-1 min-w-0 break-words text-left`}>{message.text}</div>
                ) : (
                  <img src={message.gift.imageUrl} className="size-10 mx-auto" />
                )}
                <span className="absolute flex flex-row bottom-[10px] right-[10px] text-[12px]">
                  {formatTime(message.createdAt)}
                  {isCurrentUser && (
                    <span className="ml-[6px] flex items-center">
                      {message.readAt ? (
                        <img src="/icons/msg_status_read.png" className="w-[18px] h-[18px]" />
                      ) : (
                        <img src="/icons/msg_status_delivered.png" className="w-[18px] h-[18px]" />
                      )}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
        <div className="font-raleway w-full flex items-center justify-center text-white text-[14px] opacity-80">
          <div className="w-full px-4 flex justify-start">
            {isTyping && (
              <span className="flex flex-row justify-start items-center">
                <img src="/icons/writing_message.png" className="mr-[5px] w-[12px] h-[12px]" />
                {otherParticipant?.name} пишет сообщение
              </span>
            )}
          </div>
        </div>
        <div className={`w-full flex items-center p-4 justify-center text-white font-raleway`}>
          <input
            ref={messageInputRef}
            id="msginput"
            placeholder="Сообщение"
            className="text-[18px] pl-4 text-white w-full h-[64px] bg-[#FFFFFF33] rounded-[400px]"
            value={messageInput}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            onFocus={() => {
              requestAnimationFrame(() => {
                scrollToBottom();

                setTimeout(() => {
                  scrollToBottom();
                }, 500);
              });
            }}
            maxLength={300}
          />
          <div
            className="ml-[10px] cursor-pointer w-[64px] h-[64px] flex items-center justify-center shrink-0 bg-[#A1F69E] rounded-[50%]"
            onClick={handleSendMessage}
          >
            <img src="/icons/send_msg.png" className="w-[24px] h-[24px] shrink-0" />
          </div>
        </div>
      </div>
      {showFirstMessagePrompt && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-5 flex flex-col items-center justify-center gap-7 w-full">
          <img src="/icons/bottom_bar_button_1.svg" alt="hart" className="size-[48px]" />
          <div className="inline-flex">
            <p className="text-lg text-white text-center">
              Сделайте первый шаг и напишите сообщение{' '}
              <p className="font-medium inline-flex items-center gap-0.5">
                (+20 <img src="/icons/myta-coin.svg" alt="hart" className="size-[16px]" />)
              </p>
            </p>
          </div>
        </div>
      )}
    </CheckIsBlocked>
  );
}

export default ChatView;
